import type { Country, Building, Upgrade, RandomEvent, Achievement, FactTidbit } from './types';

export const COUNTRIES: Record<string, Country> = {
  sa: {
    code: 'sa',
    name: 'Saudi Arabia',
    flag: '🇸🇦',
    currency: 'Riyal',
    currencySymbol: 'SAR',
    selfSufficiency: 72,
    targetSelfSufficiency: 90,
    perCapitaConsumption: 45,
    population: 35,
    feedSubsidy: 30,
    description: 'The regional powerhouse with 87% of GCC production. Vision 2030 aims for 90% self-sufficiency.',
    funFact: '🥚 Saudi Arabia is 117% self-sufficient in eggs - they actually EXPORT eggs to neighbors!',
    difficulty: 'Easy',
    specialMechanic: 'Feed subsidies reduce costs by 30%',
  },
  ae: {
    code: 'ae',
    name: 'UAE',
    flag: '🇦🇪',
    currency: 'Dirham',
    currencySymbol: 'AED',
    selfSufficiency: 10,
    targetSelfSufficiency: 50,
    perCapitaConsumption: 52,
    population: 9.9,
    feedSubsidy: 10,
    description: 'Premium market focused on fresh & chilled. Hand slaughter only - no machines allowed!',
    funFact: '🦶 Chicken paws have NO market in UAE - they literally use them as dried manure!',
    difficulty: 'Medium',
    specialMechanic: 'Hand slaughter: -15% efficiency, +20% premium price',
  },
  qa: {
    code: 'qa',
    name: 'Qatar',
    flag: '🇶🇦',
    currency: 'Riyal',
    currencySymbol: 'QAR',
    selfSufficiency: 5,
    targetSelfSufficiency: 100,
    perCapitaConsumption: 57,
    population: 2.9,
    feedSubsidy: 50,
    description: 'Blockade survivor! Went from 5% to 90%+ self-sufficiency in just 2 years.',
    funFact: '✈️ During the 2017 blockade, Qatar AIRLIFTED 4,000 COWS on planes!',
    difficulty: 'Hard',
    specialMechanic: 'Blockade Mode: Random import disruptions, but massive government support',
  },
  kw: {
    code: 'kw',
    name: 'Kuwait',
    flag: '🇰🇼',
    currency: 'Dinar',
    currencySymbol: 'KWD',
    selfSufficiency: 25,
    targetSelfSufficiency: 60,
    perCapitaConsumption: 65.43,
    population: 4.3,
    feedSubsidy: 20,
    description: 'Highest chicken consumption in the WORLD at 65.43 kg/person/year!',
    funFact: '🍗 Kuwaitis eat MORE CHICKEN than ANY country on Earth - 65.43 kg per person annually!',
    difficulty: 'Medium',
    specialMechanic: 'Insatiable demand: +50% base demand, prices fluctuate wildly',
  },
  bh: {
    code: 'bh',
    name: 'Bahrain',
    flag: '🇧🇭',
    currency: 'Dinar',
    currencySymbol: 'BHD',
    selfSufficiency: 10,
    targetSelfSufficiency: 40,
    perCapitaConsumption: 37,
    population: 1.5,
    feedSubsidy: 15,
    description: 'Fastest growing market (2.77% CAGR). Focus on premium & organic niches.',
    funFact: '🌱 Bahrain is the premium market leader - organic poultry growing at 2.54% annually!',
    difficulty: 'Expert',
    specialMechanic: 'Premium focus: Higher prices but smaller market size',
  },
  om: {
    code: 'om',
    name: 'Oman',
    flag: '🇴🇲',
    currency: 'Rial',
    currencySymbol: 'OMR',
    selfSufficiency: 35,
    targetSelfSufficiency: 70,
    perCapitaConsumption: 44.5,
    population: 5.1,
    feedSubsidy: 25,
    description: 'Steady growth market receiving exports from neighbors. Reliable trade partner.',
    funFact: '📦 Oman imports chickens from BOTH Saudi Arabia AND Qatar - diplomatic neutral ground!',
    difficulty: 'Medium',
    specialMechanic: 'Trade hub: Bonus income from regional trading',
  },
};

export const INITIAL_BUILDINGS: Record<string, Building> = {
  backyard: {
    id: 'backyard',
    name: 'Backyard Coop',
    description: 'A humble beginning. Every empire starts somewhere.',
    baseCost: 15,
    costMultiplier: 1.15,
    baseProduction: 0.1,
    count: 0,
    icon: '🏠',
  },
  smallFarm: {
    id: 'smallFarm',
    name: 'Small Farm',
    description: 'A proper chicken farm with automated feeders.',
    baseCost: 100,
    costMultiplier: 1.15,
    baseProduction: 1,
    count: 0,
    icon: '🏡',
    unlockAt: 10,
  },
  broilerHouse: {
    id: 'broilerHouse',
    name: 'Broiler House',
    description: 'Industrial broiler house. Climate-controlled for desert heat.',
    baseCost: 1100,
    costMultiplier: 1.15,
    baseProduction: 8,
    count: 0,
    icon: '🏭',
    unlockAt: 100,
  },
  hatchery: {
    id: 'hatchery',
    name: 'Hatchery',
    description: 'Mass-produce day-old chicks. The circle of life accelerates.',
    baseCost: 12000,
    costMultiplier: 1.15,
    baseProduction: 47,
    count: 0,
    icon: '🥚',
    unlockAt: 1000,
  },
  processingPlant: {
    id: 'processingPlant',
    name: 'Processing Plant',
    description: 'From farm to table. Whole chickens and parts.',
    baseCost: 130000,
    costMultiplier: 1.15,
    baseProduction: 260,
    count: 0,
    icon: '⚙️',
    unlockAt: 10000,
  },
  feedMill: {
    id: 'feedMill',
    name: 'Feed Mill',
    description: 'Control the 60-70% cost factor. Vertical integration!',
    baseCost: 1400000,
    costMultiplier: 1.15,
    baseProduction: 1400,
    count: 0,
    icon: '🌾',
    unlockAt: 100000,
  },
  megaComplex: {
    id: 'megaComplex',
    name: 'Mega Complex',
    description: 'Almarai-level operation. 450 million birds/year capacity.',
    baseCost: 20000000,
    costMultiplier: 1.15,
    baseProduction: 7800,
    count: 0,
    icon: '🏰',
    unlockAt: 1000000,
  },
  exportTerminal: {
    id: 'exportTerminal',
    name: 'Export Terminal',
    description: 'Compete with Brazil on the global stage.',
    baseCost: 330000000,
    costMultiplier: 1.15,
    baseProduction: 44000,
    count: 0,
    icon: '🚢',
    unlockAt: 10000000,
  },
};

export const INITIAL_UPGRADES: Record<string, Upgrade> = {
  betterFeed: {
    id: 'betterFeed',
    name: 'Better Feed',
    description: 'Improved feed formula. +10% production per level.',
    baseCost: 100,
    costMultiplier: 2,
    effect: 'production',
    effectValue: 0.1,
    level: 0,
    maxLevel: 10,
    icon: '🌽',
  },
  cooling: {
    id: 'cooling',
    name: 'Desert Cooling',
    description: 'Advanced cooling for the harsh climate. +15% production.',
    baseCost: 500,
    costMultiplier: 2.5,
    effect: 'production',
    effectValue: 0.15,
    level: 0,
    maxLevel: 5,
    icon: '❄️',
    unlockAt: 100,
  },
  premiumBrand: {
    id: 'premiumBrand',
    name: 'Premium Brand',
    description: 'Fresh & chilled positioning. +20% price per level.',
    baseCost: 1000,
    costMultiplier: 3,
    effect: 'price',
    effectValue: 0.2,
    level: 0,
    maxLevel: 5,
    icon: '⭐',
    unlockAt: 500,
  },
  automation: {
    id: 'automation',
    name: 'Farm Automation',
    description: 'Automated feeding and collection. Auto-sells chickens.',
    baseCost: 5000,
    costMultiplier: 5,
    effect: 'automation',
    effectValue: 0.1,
    level: 0,
    maxLevel: 3,
    icon: '🤖',
    unlockAt: 1000,
  },
  geneticsLab: {
    id: 'geneticsLab',
    name: 'Genetics Lab',
    description: 'Breed superior chickens. +25% production per level.',
    baseCost: 50000,
    costMultiplier: 4,
    effect: 'production',
    effectValue: 0.25,
    level: 0,
    maxLevel: 5,
    icon: '🧬',
    unlockAt: 10000,
  },
  governmentLobby: {
    id: 'governmentLobby',
    name: 'Government Lobby',
    description: 'Better subsidies. +30% cost reduction per level.',
    baseCost: 100000,
    costMultiplier: 5,
    effect: 'special',
    effectValue: 0.3,
    level: 0,
    maxLevel: 3,
    icon: '🏛️',
    unlockAt: 50000,
  },
  halalCertification: {
    id: 'halalCertification',
    name: 'Premium Halal',
    description: 'Top-tier halal certification. +40% price.',
    baseCost: 200000,
    costMultiplier: 3,
    effect: 'price',
    effectValue: 0.4,
    level: 0,
    maxLevel: 3,
    icon: '☪️',
    unlockAt: 100000,
  },
  exportDeal: {
    id: 'exportDeal',
    name: 'Export License',
    description: 'Access international markets. Massive revenue boost!',
    baseCost: 1000000,
    costMultiplier: 10,
    effect: 'special',
    effectValue: 2,
    level: 0,
    maxLevel: 1,
    icon: '📜',
    unlockAt: 500000,
  },
};

export const RANDOM_EVENTS: RandomEvent[] = [
  // Positive events
  {
    id: 'ramadan',
    title: 'Ramadan Demand Surge!',
    description: 'Chicken consumption spikes during Ramadan. +50% demand!',
    effect: 'demand',
    effectValue: 1.5,
    duration: 60,
    probability: 0.15,
    isPositive: true,
    icon: '🌙',
  },
  {
    id: 'subsidy',
    title: 'Government Subsidy!',
    description: 'New ADF loan approved. -30% costs for 45 seconds!',
    effect: 'cost',
    effectValue: 0.7,
    duration: 45,
    probability: 0.12,
    isPositive: true,
    icon: '💰',
  },
  {
    id: 'exportDemand',
    title: 'Export Order!',
    description: 'Neighboring country wants your chickens. +100% price!',
    effect: 'price',
    effectValue: 2,
    duration: 30,
    probability: 0.08,
    isPositive: true,
    icon: '📦',
  },
  {
    id: 'perfectWeather',
    title: 'Perfect Weather!',
    description: 'Mild temperatures boost production. +40% output!',
    effect: 'production',
    effectValue: 1.4,
    duration: 45,
    probability: 0.1,
    isPositive: true,
    icon: '☀️',
  },

  // Negative events
  {
    id: 'heatwave',
    title: 'Desert Heatwave!',
    description: 'Extreme heat strains cooling systems. -30% production.',
    effect: 'production',
    effectValue: 0.7,
    duration: 40,
    probability: 0.12,
    isPositive: false,
    icon: '🥵',
  },
  {
    id: 'feedSpike',
    title: 'Feed Price Spike!',
    description: 'Global corn prices surge. +40% costs!',
    effect: 'cost',
    effectValue: 1.4,
    duration: 50,
    probability: 0.1,
    isPositive: false,
    icon: '📈',
  },
  {
    id: 'birdFlu',
    title: 'Bird Flu Scare!',
    description: 'Disease outbreak threatens flock. -50% production!',
    effect: 'production',
    effectValue: 0.5,
    duration: 35,
    probability: 0.05,
    isPositive: false,
    icon: '🦠',
  },
  {
    id: 'priceWar',
    title: 'Price War!',
    description: 'Competitors slash prices. -40% sell price!',
    effect: 'price',
    effectValue: 0.6,
    duration: 40,
    probability: 0.08,
    isPositive: false,
    icon: '⚔️',
  },

  // Country-specific events
  {
    id: 'blockade',
    title: 'BLOCKADE!',
    description: 'Borders closed! No imports for 60 seconds. Government support tripled!',
    effect: 'special',
    effectValue: 3,
    duration: 60,
    probability: 0.15,
    isPositive: true,
    icon: '🚧',
    country: 'qa',
  },
  {
    id: 'brfCompetition',
    title: 'BRF Expansion!',
    description: 'Brazilian giant launches new products. -25% market share!',
    effect: 'demand',
    effectValue: 0.75,
    duration: 45,
    probability: 0.1,
    isPositive: false,
    icon: '🇧🇷',
    country: 'sa',
  },
  {
    id: 'handSlaughter',
    title: 'Slaughter Shortage!',
    description: 'Not enough trained slaughterers. -20% production.',
    effect: 'production',
    effectValue: 0.8,
    duration: 40,
    probability: 0.12,
    isPositive: false,
    icon: '🔪',
    country: 'ae',
  },
];

export const ACHIEVEMENTS: Record<string, Achievement> = {
  firstChicken: {
    id: 'firstChicken',
    title: 'First Cluck',
    description: 'Produce your first chicken.',
    requirement: 1,
    type: 'chickens',
    unlocked: false,
    icon: '🐣',
  },
  hundred: {
    id: 'hundred',
    title: 'Century Club',
    description: 'Produce 100 chickens.',
    requirement: 100,
    type: 'chickens',
    unlocked: false,
    icon: '💯',
  },
  thousand: {
    id: 'thousand',
    title: 'Chicken Farmer',
    description: 'Produce 1,000 chickens.',
    requirement: 1000,
    type: 'chickens',
    unlocked: false,
    icon: '🌾',
  },
  tenThousand: {
    id: 'tenThousand',
    title: 'Poultry Baron',
    description: 'Produce 10,000 chickens.',
    requirement: 10000,
    type: 'chickens',
    unlocked: false,
    icon: '👑',
  },
  millionaire: {
    id: 'millionaire',
    title: 'Millionaire',
    description: 'Earn 1,000,000 in currency.',
    requirement: 1000000,
    type: 'money',
    unlocked: false,
    icon: '💎',
  },
  halfWay: {
    id: 'halfWay',
    title: 'Vision Progress',
    description: 'Reach 50% self-sufficiency.',
    requirement: 50,
    type: 'selfSufficiency',
    unlocked: false,
    icon: '📊',
  },
  vision2030: {
    id: 'vision2030',
    title: 'Vision 2030',
    description: 'Achieve 90% self-sufficiency!',
    requirement: 90,
    type: 'selfSufficiency',
    unlocked: false,
    icon: '🎯',
    reward: 'Unlock Export Terminal',
  },
  fullyIndependent: {
    id: 'fullyIndependent',
    title: 'Food Security!',
    description: 'Achieve 100% self-sufficiency.',
    requirement: 100,
    type: 'selfSufficiency',
    unlocked: false,
    icon: '🏆',
    reward: 'Prestige Mode Unlocked',
  },
  industrialist: {
    id: 'industrialist',
    title: 'Industrialist',
    description: 'Own 100 buildings total.',
    requirement: 100,
    type: 'buildings',
    unlocked: false,
    icon: '🏗️',
  },
};

export const FUN_FACTS: FactTidbit[] = [
  { text: "Kuwaitis eat 65.43 kg of chicken per person annually - highest in the world!", source: "FAO 2023" },
  { text: "During Qatar's 2017 blockade, they airlifted 4,000 cows on planes!", source: "Baladna" },
  { text: "Saudi Arabia produces 5.4 BILLION eggs per year!", source: "GASTAT" },
  { text: "Chicken paws have zero market value in UAE - used as manure!", source: "Industry report" },
  { text: "BRF owns 85% of Saudi's frozen chicken market with just 2 brands.", source: "Market analysis" },
  { text: "Feed costs are 60-70% of chicken production costs.", source: "USDA" },
  { text: "Qatar went from 5% to 90%+ chicken self-sufficiency in 2 years!", source: "Chatham House" },
  { text: "UAE requires hand slaughter - machines are forbidden!", source: "UAE regulations" },
  { text: "Saudi's preferred chicken size is 800-1100g - smaller is better!", source: "Trade data" },
  { text: "The GCC imports $2.8 BILLION in poultry annually!", source: "FAO" },
  { text: "Brazil supplies 38% of global frozen chicken exports.", source: "USDA" },
  { text: "Almarai aims to produce 450 million birds by 2027!", source: "Almarai" },
  { text: "Broken wings from hand slaughter are common in UAE.", source: "Quality reports" },
  { text: "Saudi's Agricultural Development Fund gave $5.4B in poultry loans.", source: "ADF" },
  { text: "70% of GCC chicken imports are whole birds, 30% are parts.", source: "Trade data" },
  { text: "Qataris eat 57 kg of chicken per person per year!", source: "FAO" },
  { text: "Saudi egg production is 117% of demand - they export surplus!", source: "GASTAT" },
  { text: "There was a $533M acquisition battle for Al Watania Poultry!", source: "Bloomberg" },
  { text: "Kuwait had 20% chicken price spikes in 2022 due to supply issues.", source: "Local news" },
  { text: "Bahrain is the fastest-growing poultry market at 2.77% CAGR.", source: "Mordor Intel" },
];
